/**
 * @fileoverview Date-window helpers for Schedule block loading: build windows,
 * merge fetched chunks, and decide when to prefetch more data near the loaded edge.
 */

import { getShiftedDate, toLocalCalendarDay, toLocalDateKey } from "../../../../utils/datetime/core";
import {
  SCHEDULE_DATA_WINDOW_WEEKS,
  SCHEDULE_PREFETCH_THRESHOLD_DAYS,
} from "../constants";
import { getAspectBlockId } from "./scheduleBlockSelectors";

/**
 * @typedef {{ startKey: string, endKey: string }} ScheduleDataWindow Inclusive YYYY-MM-DD keys.
 */

/**
 * Stable id for a fetched API chunk (inclusive date keys).
 *
 * @param {{ startKey: string, endKey: string }} chunk
 * @returns {string}
 */
export function scheduleChunkKey(chunk) {
  return `${chunk.startKey}|${chunk.endKey}`;
}

/**
 * Calendar-day count from key `a` to key `b` (b - a in days).
 *
 * @param {string} a YYYY-MM-DD
 * @param {string} b YYYY-MM-DD
 * @returns {number}
 */
export function daysBetweenDateKeys(a, b) {
  const [ay, am, ad] = a.split("-").map((n) => parseInt(n, 10));
  const [by, bm, bd] = b.split("-").map((n) => parseInt(n, 10));
  const da = new Date(ay, am - 1, ad, 12, 0, 0, 0);
  const db = new Date(by, bm - 1, bd, 12, 0, 0, 0);
  return Math.round((db.getTime() - da.getTime()) / 86400000);
}

/**
 * Shift an ISO date key by `dayCount` calendar days.
 *
 * @param {string} dateKey YYYY-MM-DD
 * @param {number} dayCount Signed day offset.
 * @returns {string}
 */
export function shiftDateKey(dateKey, dayCount) {
  const [y, m, d] = dateKey.split("-").map((n) => parseInt(n, 10));
  const shifted = getShiftedDate(new Date(y, m - 1, d, 12, 0, 0, 0), dayCount);
  return toLocalDateKey(shifted);
}

/**
 * Inclusive window of `weeksEachSide` × 7 days before/after `centerDate`.
 *
 * @param {Date|string|number} centerDate Anchor calendar day.
 * @param {number} [weeksEachSide]
 * @returns {ScheduleDataWindow}
 */
export function buildScheduleDataWindowAroundDate(
  centerDate,
  weeksEachSide = SCHEDULE_DATA_WINDOW_WEEKS
) {
  const center = toLocalCalendarDay(centerDate);
  const daySpan = weeksEachSide * 7;
  return {
    startKey: toLocalDateKey(getShiftedDate(center, -daySpan)),
    endKey: toLocalDateKey(getShiftedDate(center, daySpan)),
  };
}

/**
 * Smallest inclusive window covering both inputs.
 *
 * @param {ScheduleDataWindow|null|undefined} a
 * @param {ScheduleDataWindow|null|undefined} b
 * @returns {ScheduleDataWindow|null}
 */
export function unionScheduleDataWindows(a, b) {
  if (!a) {
    return b ?? null;
  }
  if (!b) {
    return a;
  }
  return {
    startKey: a.startKey < b.startKey ? a.startKey : b.startKey,
    endKey: a.endKey > b.endKey ? a.endKey : b.endKey,
  };
}

/**
 * Blocks whose span overlaps the inclusive window.
 *
 * @param {Array<{ startDate: Date, endDate: Date }>} blocks
 * @param {ScheduleDataWindow} window
 * @returns {boolean}
 */
export function scheduleBlockOverlapsWindow(block, window) {
  const startKey = toLocalDateKey(block.startDate);
  const endKey = toLocalDateKey(block.endDate);
  return startKey <= window.endKey && endKey >= window.startKey;
}

/**
 * Drop blocks that no longer overlap the loaded window.
 *
 * @param {Array<Object>} blocks
 * @param {ScheduleDataWindow} window
 * @returns {Array<Object>}
 */
export function filterBlocksToScheduleWindow(blocks, window) {
  return blocks.filter((block) => scheduleBlockOverlapsWindow(block, window));
}

/**
 * Upsert blocks by id (incoming replaces existing with same id).
 *
 * @param {Array<Object>} existingBlocks
 * @param {Array<Object>} incomingBlocks
 * @returns {Array<Object>}
 */
export function mergeScheduleBlocksById(existingBlocks, incomingBlocks) {
  const byId = new Map(existingBlocks.map((block) => [String(block.id), block]));
  for (const block of incomingBlocks) {
    byId.set(String(block.id), block);
  }
  return Array.from(byId.values());
}

/**
 * Replace aspect rows for touched block ids, then append incoming rows.
 *
 * @param {Array<Object>} existingAspects
 * @param {Array<Object>} incomingAspects
 * @param {Array<string|number>} touchedBlockIds
 * @param {ScheduleDataWindow|null} [touchedWindow=null] Window whose aspect rows are authoritative.
 * @returns {Array<Object>}
 */
export function mergeScheduleAspectRows(
  existingAspects,
  incomingAspects,
  touchedBlockIds,
  touchedWindow = null
) {
  const touched = new Set(touchedBlockIds.map((id) => String(id)));
  const kept = existingAspects.filter((row) => {
    if (!touched.has(String(getAspectBlockId(row)))) {
      return true;
    }
    if (!touchedWindow) {
      return false;
    }
    const rowKey = toLocalDateKey(row.date);
    return rowKey < touchedWindow.startKey || rowKey > touchedWindow.endKey;
  });
  return [...kept, ...incomingAspects];
}

/**
 * Chunks to fetch when the visible range nears a loaded-window edge.
 *
 * @param {ScheduleDataWindow} loadedWindow Currently loaded inclusive range.
 * @param {string} visibleStartKey First visible column (YYYY-MM-DD).
 * @param {string} visibleEndKey Last visible column (YYYY-MM-DD).
 * @param {Object} [options]
 * @param {number} [options.thresholdDays]
 * @param {number} [options.weeksEachSide]
 * @returns {Array<ScheduleDataWindow>}
 */
export function getScheduleWindowPrefetchChunks(
  loadedWindow,
  visibleStartKey,
  visibleEndKey,
  options = {}
) {
  const thresholdDays =
    options.thresholdDays ?? SCHEDULE_PREFETCH_THRESHOLD_DAYS;
  const weeksEachSide =
    options.weeksEachSide ?? SCHEDULE_DATA_WINDOW_WEEKS;
  const chunkDays = weeksEachSide * 7;
  if (chunkDays <= 0) {
    return [];
  }
  const chunks = [];

  let currentStartKey = loadedWindow.startKey;
  while (
    daysBetweenDateKeys(currentStartKey, visibleStartKey) <= thresholdDays
  ) {
    const chunkEndKey = shiftDateKey(currentStartKey, -1);
    const chunkStartKey = shiftDateKey(currentStartKey, -chunkDays);
    if (chunkStartKey <= chunkEndKey) {
      chunks.push({ startKey: chunkStartKey, endKey: chunkEndKey });
    }
    currentStartKey = chunkStartKey;
  }

  let currentEndKey = loadedWindow.endKey;
  while (
    daysBetweenDateKeys(visibleEndKey, currentEndKey) <= thresholdDays
  ) {
    const chunkStartKey = shiftDateKey(currentEndKey, 1);
    const chunkEndKey = shiftDateKey(currentEndKey, chunkDays);
    if (chunkStartKey <= chunkEndKey) {
      chunks.push({ startKey: chunkStartKey, endKey: chunkEndKey });
    }
    currentEndKey = chunkEndKey;
  }

  return chunks.filter(
    (chunk) => !isDateRangeCoveredByWindow(chunk, loadedWindow)
  );
}

/**
 * True when every day in `range` already lies inside `loadedWindow`.
 *
 * @param {ScheduleDataWindow} range
 * @param {ScheduleDataWindow} loadedWindow
 * @returns {boolean}
 */
export function isDateRangeCoveredByWindow(range, loadedWindow) {
  return (
    range.startKey >= loadedWindow.startKey
    && range.endKey <= loadedWindow.endKey
  );
}

/**
 * Expand `loadedWindow` to include every chunk range.
 *
 * @param {ScheduleDataWindow} loadedWindow
 * @param {Array<ScheduleDataWindow>} chunks
 * @returns {ScheduleDataWindow}
 */
export function extendLoadedWindowWithChunks(loadedWindow, chunks) {
  let next = { ...loadedWindow };
  for (const chunk of chunks) {
    next = unionScheduleDataWindows(next, chunk);
  }
  return next;
}
